/**
 * Normalize a model-proposed diagram edit into renderable mermaid HTML.
 *
 * The edit model sometimes returns a bare Mermaid DSL string, an `<svg>`, or a
 * fragment that isn't wrapped in `<div class="mermaid">`. The report frame only
 * re-draws diagrams from a `.mermaid` element, so before splicing a diagram
 * `target_edit` we re-wrap the draft (falling back to the target's captured
 * `mermaid_source` when only an `<svg>` came back).
 *
 * Ported from `normalizeTargetDraftHtml` in the `apps/ara` injected script
 * (`static/index.html`); only `type === 'diagram'` targets are rewritten, every
 * other target (and non-diagram HTML) passes through untouched.
 */

export type NormalizeTargetInfo = {
  type?: string
  mermaid_source?: string
}

const DIAGRAM_KW =
  /^(gantt|flowchart|graph|sequenceDiagram|classDiagram|stateDiagram|pie|xychart-beta|gitGraph|journey)\b/
const MERMAID_CLASS = /class\s*=\s*["'][^"']*\bmermaid\b/i
const SVG_TAG = /<svg[\s>]/i

function isDsl(text: string): boolean {
  return (
    DIAGRAM_KW.test(text) ||
    text.startsWith('style ') ||
    text.startsWith('classDef ') ||
    text.startsWith('class ')
  )
}

export function normalizeTargetDraftHtml(target: NormalizeTargetInfo, html: string): string {
  const raw = String(html ?? '').trim()
  if (!raw || target?.type !== 'diagram') return html
  if (MERMAID_CLASS.test(raw)) return html

  if (raw.startsWith('<')) {
    const textOnly = raw
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (isDsl(textOnly)) return `<div class="mermaid">${textOnly}</div>`
    if (SVG_TAG.test(raw) && target?.mermaid_source)
      return `<div class="mermaid">${target.mermaid_source}</div>`
    return html
  }

  if (isDsl(raw)) {
    const styleOnly =
      raw.startsWith('style ') || raw.startsWith('classDef ') || raw.startsWith('class ')
    if (target?.mermaid_source && styleOnly)
      return `<div class="mermaid">${target.mermaid_source}\n${raw}</div>`
    return `<div class="mermaid">${raw}</div>`
  }

  return html
}
